import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Clock, CheckCircle, XCircle, AlertCircle, MessageSquare, User } from 'lucide-react';
import { Button } from '../ui/button';
import FeedbackModal from './FeedbackModal';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGENT_NAMES = {
  DOCUMENT: 'DokumentAgent',
  RISK: 'RisikoAgent',
  TASK: 'OppgaveAgent',
  TIMELINE: 'TidslinjeAgent'
};

const STATUS_CONFIG = {
  PENDING: { icon: AlertCircle, color: 'amber', label: 'Venter' },
  APPROVED: { icon: CheckCircle, color: 'green', label: 'Godkjent' },
  REJECTED: { icon: XCircle, color: 'red', label: 'Avvist' },
  IMPLEMENTED: { icon: CheckCircle, color: 'indigo', label: 'Implementert' }
};

const AgentRunHistory = ({ runs, onRefresh }) => {
  const [expandedRuns, setExpandedRuns] = useState(new Set());
  const [runDetails, setRunDetails] = useState({});
  const [loadingRun, setLoadingRun] = useState(null);
  const [feedbackModal, setFeedbackModal] = useState({ show: false, agentRunId: null, suggestionId: null });
  const [teamFeedback, setTeamFeedback] = useState({});

  const toggleRun = async (runId) => {
    const newExpanded = new Set(expandedRuns);
    
    if (newExpanded.has(runId)) {
      newExpanded.delete(runId);
      setExpandedRuns(newExpanded);
    } else {
      newExpanded.add(runId);
      setExpandedRuns(newExpanded);
      
      // Load suggestions if not already loaded
      if (!runDetails[runId]) {
        setLoadingRun(runId);
        try {
          const response = await axios.get(`${API}/agents/runs/${runId}/all-suggestions`);
          setRunDetails(prev => ({
            ...prev,
            [runId]: response.data
          }));
        } catch (error) {
          console.error('Error loading run details:', error);
          toast.error('Kunne ikke laste forslag');
        } finally {
          setLoadingRun(null);
        }
      }
    }
  };

  const loadTeamFeedback = async (suggestionId) => {
    try {
      const response = await axios.get(`${API}/agents/suggestions/${suggestionId}/feedback`);
      setTeamFeedback(prev => ({
        ...prev,
        [suggestionId]: response.data
      }));
    } catch (error) {
      console.error('Error loading team feedback:', error);
    }
  };

  const openFeedbackModal = (agentRunId, suggestionId) => {
    setFeedbackModal({ show: true, agentRunId, suggestionId });
    loadTeamFeedback(suggestionId);
  };

  if (!runs || runs.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold text-gray-900">Kjøringshistorikk</h3>
      
      <div className="space-y-2">
        {runs.map(run => {
          const isExpanded = expandedRuns.has(run.id);
          const details = runDetails[run.id];
          const agentName = AGENT_NAMES[run.agent_type] || run.agent_type;
          
          return (
            <div key={run.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {/* Run Header */}
              <button
                onClick={() => toggleRun(run.id)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-gray-600" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-600" />
                  )}
                  
                  <div className="text-left">
                    <div className="font-medium text-gray-900">{agentName}</div>
                    <div className="text-xs text-gray-600 flex items-center gap-2">
                      <Clock className="w-3 h-3" />
                      {new Date(run.created_at).toLocaleString('nb-NO')}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-gray-600 text-xs">Forslag</div>
                    <div className="font-semibold text-gray-900">{run.suggestion_count}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-600 text-xs">Godkjent</div>
                    <div className="font-semibold text-green-600">{run.approved_count}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-600 text-xs">Avvist</div>
                    <div className="font-semibold text-red-600">{run.rejected_count}</div>
                  </div>
                  {run.status === 'COMPLETED' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : run.status === 'FAILED' ? (
                    <XCircle className="w-5 h-5 text-red-600" />
                  ) : null}
                </div>
              </button>
              
              {/* Run Details */}
              {isExpanded && (
                <div className="border-t border-gray-200 bg-gray-50">
                  {loadingRun === run.id ? (
                    <div className="p-8 text-center text-sm text-gray-600">
                      Laster forslag...
                    </div>
                  ) : details ? (
                    <div className="p-4 space-y-3">
                      {details.suggestions.map(suggestion => {
                        const statusConfig = STATUS_CONFIG[suggestion.status];
                        const StatusIcon = statusConfig.icon;
                        const feedback = teamFeedback[suggestion.id];
                        
                        return (
                          <div
                            key={suggestion.id}
                            className="bg-white border border-gray-200 rounded-lg p-4"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`px-2 py-0.5 text-xs font-medium bg-${statusConfig.color}-50 text-${statusConfig.color}-700 rounded-full flex items-center gap-1`}>
                                    <StatusIcon className="w-3 h-3" />
                                    {statusConfig.label}
                                  </span>
                                  {suggestion.feedback_count > 0 && (
                                    <span className="px-2 py-0.5 text-xs font-medium bg-gray-50 text-indigo-700 rounded-full flex items-center gap-1">
                                      <MessageSquare className="w-3 h-3" />
                                      {suggestion.feedback_count} feedback
                                    </span>
                                  )}
                                </div>
                                <h4 className="font-semibold text-gray-900 text-sm">
                                  {suggestion.data?.task_title || suggestion.data?.title || suggestion.data?.document_name}
                                </h4>
                                <p className="text-sm text-gray-600 mt-1">
                                  {(suggestion.data?.task_description || suggestion.data?.description || suggestion.data?.reason || '').substring(0, 150)}...
                                </p>
                              </div>
                            </div>
                            
                            {suggestion.reviewed_by_name && (
                              <div className="text-xs text-gray-600 flex items-center gap-1 mb-2">
                                <User className="w-3 h-3" />
                                {suggestion.status === 'APPROVED' ? 'Godkjent' : 'Avvist'} av {suggestion.reviewed_by_name}
                              </div>
                            )}
                            
                            {/* Team Feedback Preview */}
                            {feedback && feedback.total_feedback > 0 && (
                              <div className="bg-gray-50 border border-indigo-200 rounded p-2 mb-2">
                                <div className="text-xs font-medium text-indigo-900 mb-1">
                                  Team feedback ({feedback.total_feedback}):
                                </div>
                                {feedback.feedback.slice(0, 2).map(fb => (
                                  <div key={fb.id} className="text-xs text-indigo-700">
                                    • {fb.user_name}: {fb.correctness === 'YES' ? '✅' : fb.correctness === 'NO' ? '❌' : '⚠️'} {fb.comment ? `"${fb.comment.substring(0, 50)}..."` : ''}
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openFeedbackModal(run.id, suggestion.id)}
                              className="w-full text-xs"
                            >
                              <MessageSquare className="w-3 h-3 mr-1" />
                              Gi din feedback
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Feedback Modal */}
      {feedbackModal.show && (
        <FeedbackModal
          agentRunId={feedbackModal.agentRunId}
          suggestionId={feedbackModal.suggestionId}
          onClose={() => setFeedbackModal({ show: false, agentRunId: null, suggestionId: null })}
          onSubmit={() => {
            setFeedbackModal({ show: false, agentRunId: null, suggestionId: null });
            if (onRefresh) onRefresh();
          }}
        />
      )}
    </div>
  );
};

export default AgentRunHistory;
